// DP-4577: AU postcodes must sit in a real Australia Post range (0200-0299,
// 0800-0999, 1000-9999), so values like "0000" are rejected. CA codes use the
// Canada Post letter set: D, F, I, O, Q, U never appear; W and Z can't lead.
const AU_REGEX = /^(0[289]\d{2}|[1-9]\d{3})$/;
const CA_REGEX = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

const AU_STATE_RANGES = {
  ACT: [[200, 299], [2600, 2618], [2900, 2920]],
  NSW: [[1000, 2599], [2619, 2899], [2921, 2999]],
  NT: [[800, 999]],
  QLD: [[4000, 4999], [9000, 9999]],
  SA: [[5000, 5999]],
  TAS: [[7000, 7999]],
  VIC: [[3000, 3999], [8000, 8999]],
  WA: [[6000, 6999]],
};

// True when the postcode falls inside the given AU state's ranges. Unknown or
// missing states pass, so this only tightens validation where a state is set.
export function auPostcodeMatchesState(postcode, state) {
  const ranges = AU_STATE_RANGES[(state || '').trim().toUpperCase()];
  if (!ranges) return true;
  const code = parseInt(postcode, 10);
  return ranges.some(([min, max]) => code >= min && code <= max);
}

// Postcode capture (DP-3890) applies to AU and CA outlets only.
export function postcodeCountry(code) {
  const c = (code || '').toString().toUpperCase();
  return c === 'AU' || c === 'CA' ? c : null;
}

export function isValidPostcode(value, country) {
  const v = (value || '').trim();
  if (!v) return false;
  return country === 'AU' ? AU_REGEX.test(v) : CA_REGEX.test(v);
}

// Validate and normalize. CA codes become "A1A 1A1". Returns null when empty or invalid.
export function normalizePostcode(value, country) {
  const v = (value || '').trim();
  if (!v) return null;
  if (country === 'AU') return AU_REGEX.test(v) ? v : null;
  if (!CA_REGEX.test(v)) return null;
  const compact = v.replace(/\s/g, '').toUpperCase();
  return `${compact.slice(0, 3)} ${compact.slice(3, 6)}`;
}

// Live formatting as staff type. AU: digits, max 4. CA: uppercase "A1A 1A1", max 6 chars.
export function formatPostcodeInput(value, country) {
  const v = value || '';
  if (country === 'AU') return v.replace(/\D/g, '').slice(0, 4);
  const compact = v.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
  return compact.length <= 3 ? compact : `${compact.slice(0, 3)} ${compact.slice(3)}`;
}

// Postal-code suggestions via Google Places (loaded globally in index.html).
// Resolves [] when Maps is unavailable or input is too short.
export function suggestPostcodes(value, country) {
  const input = (value || '').trim();
  const g = window.google;
  // AU suggestions appear from the first digit; CA waits for the FSA prefix.
  const minLength = country === 'AU' ? 1 : 3;
  if (!country || input.length < minLength || !g || !g.maps || !g.maps.places) {
    return Promise.resolve([]);
  }

  return new Promise(resolve => {
    const service = new g.maps.places.AutocompleteService();
    service.getPlacePredictions(
      { types: ['(regions)'], input, componentRestrictions: { country } },
      (predictions, status) => {
        if (status !== g.maps.places.PlacesServiceStatus.OK || !predictions) {
          resolve([]);
          return;
        }
        const seen = new Set();
        const codes = [];
        predictions.forEach(p => {
          if (p.types && p.types.includes('postal_code')) {
            const code = p.structured_formatting && p.structured_formatting.main_text;
            if (code && isValidPostcode(code, country) && !seen.has(code)) {
              seen.add(code);
              codes.push(code);
            }
          }
        });
        codes.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
        resolve(codes.slice(0, 6));
      },
    );
  });
}
